import { useState } from 'react';
import { DeleteOutlined, SearchOutlined, UploadOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  Descriptions,
  Empty,
  Flex,
  Form,
  Grid,
  Input,
  List,
  Popconfirm,
  Select,
  Space,
  Tabs,
  Table,
  Tag,
  Tooltip,
  Typography,
  Upload,
} from 'antd';
import { useEstadoDeLista } from '@titularis/ui';
import type { CorpusDocument, CorpusHit, CorpusIngestReport } from '@titularis/api-client';
import { api } from '../api';
import { paginacao } from '../paginacao';
import { LacunasDoAcervo } from './LacunasDoAcervo';
import { LegislacaoDoAcervo } from './LegislacaoDoAcervo';
import { formatarData } from '../dominio';

/**
 * Acervo (F10).
 *
 * **Interno, e nunca servido ao aluno (D-26).** Esta tela existe para alimentar e
 * conferir o que fundamenta o comentário; quem entrega material ao aluno é a
 * Legislação Comentada, por imagem marcada no servidor.
 *
 * A tela mostra o **nível** de cada documento porque é ele que diz o que a
 * pipeline pode afirmar (D-23): registro é citável por número e verificável;
 * texto é consultável e só pode ser parafraseado. Esconder essa diferença faria
 * o revisor confiar igual nas duas coisas, que é exatamente o que não pode.
 *
 * **No celular as tabelas viram cartões e o relatório empilha.** A lista de
 * documentos tem oito colunas e a de legislação, sete: rolar de lado esconderia
 * as ações de desatualizar e remover, que são o que se vem fazer aqui. E o
 * formulário de subir documento tinha larguras fixas somando mais de 600px, que
 * numa tela de 390 vazavam para fora do cartão.
 */

/** O corte das demais telas do admin: abaixo de `md`, tabela vira lista. */
const useCompacto = () => !Grid.useBreakpoint().md;

const NIVEL: Record<string, { label: string; cor: string; ajuda: string }> = {
  registro: {
    label: 'Registro',
    cor: 'green',
    ajuda: 'Citável por número, e a citação é conferida contra a fonte.',
  },
  texto: {
    label: 'Texto',
    cor: 'default',
    ajuda: 'Consultável e parafraseável. Não pode ser citado por número.',
  },
};

const FAMILIA: Record<string, string> = {
  sumulas: 'Súmulas',
  enunciados: 'Enunciados',
  nugepnac: 'Precedentes',
  informativo: 'Informativo',
  doutrina: 'Doutrina',
  desconhecido: 'Não reconhecido',
};

/**
 * O que se pode declarar no upload, e por que `desconhecido` não está aqui.
 *
 * Declarar serve a dois casos: a prosa, que nunca terá assinatura, e o documento
 * cuja família se sabe mas cuja marca é rala demais para a detecção. Oferecer
 * "Não reconhecido" seria oferecer uma opção que não muda nada - é o que a
 * detecção já devolve sozinha.
 */
const FAMILIAS_DECLARAVEIS = ['sumulas', 'enunciados', 'nugepnac', 'informativo', 'doutrina'];

/**
 * O que o relatório diz, por diagnóstico.
 *
 * **Contagem não é diagnóstico.** "Extraiu 0 de 3" descrevia tanto o parser
 * falhando quanto o Nível 2 funcionando como projetado, e a tela mostrava um
 * aviso amarelo nos dois casos - o que treina quem usa a ignorar o aviso. Só a
 * sub-extração é defeito; as demais são o desenho, e uma delas é grave por
 * outro motivo.
 */
const AVISO: Record<
  string,
  { tipo: 'warning' | 'error' | 'info'; titulo: string; texto: (r: CorpusIngestReport) => string }
> = {
  sub_extracao: {
    tipo: 'warning',
    titulo: 'A contagem não bate com o que o documento declara',
    texto: (r) =>
      `O documento anuncia ${r.declarados} registro(s) e o parser extraiu ${r.gravados}. ` +
      'Vale conferir o material antes de usá-lo: o que faltou não vai aparecer nas buscas.',
  },
  layout_nao_suportado: {
    tipo: 'info',
    titulo: 'Diagramação ainda sem parser: entrou como Nível Texto',
    texto: (r) =>
      `A família foi reconhecida, mas esta diagramação ainda não tem parser. O documento entrou ` +
      `como texto, em ${r.trechos} trecho(s): é consultável e parafraseável, mas não citável por ` +
      'número. Não é falha - é o Nível 2 fazendo o seu papel.',
  },
  familia_desconhecida: {
    tipo: 'info',
    titulo: 'Documento entrou como Nível Texto',
    texto: (r) =>
      `Nenhuma família de registro foi reconhecida, então o documento entrou como texto, em ` +
      `${r.trechos} trecho(s). É consultável e parafraseável, e não citável por número.`,
  },
  /*
   * **Não é o mesmo aviso de `layout_nao_suportado`, e a diferença importa para
   * quem lê.** Aquele convida a escrever o parser da família; este diz que a
   * decisão já foi tomada e não há parser a escrever. Doutrina é prosa: o que
   * ela tem para dar é o argumento, e o argumento se parafraseia.
   */
  nivel_declarado: {
    tipo: 'info',
    titulo: 'Nível Texto por decisão, e não por falta de parser',
    texto: (r) =>
      `A família foi declarada por quem carregou, e ela não produz registro citável. O documento ` +
      `entrou como texto, em ${r.trechos} trecho(s): parafraseável com a proveniência à vista, ` +
      'nunca citável por número. Não há parser pendente aqui.',
  },
  sem_texto: {
    tipo: 'error',
    titulo: 'Este PDF não tem texto: é imagem',
    texto: () =>
      'Nada foi indexado, e o documento não vai aparecer em busca nenhuma. É um PDF digitalizado ' +
      'sem camada de texto - só serve ao acervo depois de passar por OCR.',
  },
};

/** O relatório é o que separa "o parser rodou" de "o parser acertou". */
function RelatorioDaIngestao({ relatorio }: { relatorio: CorpusIngestReport }) {
  const aviso = AVISO[relatorio.diagnostico];

  return (
    <Space orientation="vertical" size="small" style={{ width: '100%' }}>
      {aviso ? (
        <Alert
          type={aviso.tipo}
          showIcon
          title={aviso.titulo}
          description={aviso.texto(relatorio)}
        />
      ) : null}

      {/*
        Duas colunas viram uma no celular: em 390px, rótulo e valor lado a lado
        deixam "Declarados no documento" com uns 70px e a palavra quebra letra a
        letra.
      */}
      <Descriptions size="small" column={{ xs: 1, sm: 1, md: 2 }} bordered>
        <Descriptions.Item label="Família">
          {FAMILIA[relatorio.familia] ?? relatorio.familia}
        </Descriptions.Item>
        <Descriptions.Item label="Nível">
          {NIVEL[relatorio.tier]?.label ?? relatorio.tier}
        </Descriptions.Item>
        <Descriptions.Item label="Registros gravados">{relatorio.gravados}</Descriptions.Item>
        <Descriptions.Item label="Trechos">{relatorio.trechos}</Descriptions.Item>
        <Descriptions.Item label="Declarados no documento">
          {relatorio.declarados ?? 'não declara'}
        </Descriptions.Item>
        <Descriptions.Item label="Reingestão">
          {relatorio.reingestao ? 'sim, pelo conteúdo do arquivo' : 'não'}
        </Descriptions.Item>
      </Descriptions>

      {relatorio.colisoesInternas.length > 0 ? (
        <Typography.Text type="secondary">
          Repetidos dentro do próprio documento, gravada a primeira ocorrência:{' '}
          {relatorio.colisoesInternas.join(', ')}
        </Typography.Text>
      ) : null}

      {relatorio.jaExistiam.length > 0 ? (
        <Typography.Text type="secondary">
          Já existiam vindos de outro documento, e não foram regravados:{' '}
          {relatorio.jaExistiam.join(', ')}
        </Typography.Text>
      ) : null}
    </Space>
  );
}

/**
 * O Acervo, em dois tipos que não se misturam.
 *
 * Jurisprudência e doutrina entram por upload e são alcançadas por busca;
 * legislação entra por importação estruturada e é alcançada por endereço exato
 * (D-25). São dois acervos com regras diferentes sobre o que a pipeline pode
 * afirmar, e é por isso que a tela os separa em vez de listar tudo junto.
 */
/**
 * O rótulo da aba de lacunas, com o que está esperando.
 *
 * **O número fica fora da aba de propósito.** Uma pendência que só aparece
 * depois de clicar é uma pendência que ninguém descobre - é o mesmo motivo pelo
 * qual a trilha ficou meses sendo gravada sem leitor. Aqui o backlog se anuncia
 * de onde já se está.
 */
function BadgeDeLacunas() {
  const lacunas = useQuery({
    queryKey: ['corpus', 'lacunas'],
    queryFn: () => api.listCorpusGaps(),
  });
  const abertas = lacunas.data?.length ?? 0;

  return (
    <Flex align="center" gap={8}>
      Faltando no acervo
      {abertas > 0 ? (
        <Tag color="orange" style={{ marginInlineEnd: 0 }}>
          {abertas}
        </Tag>
      ) : null}
    </Flex>
  );
}

export function AcervoPage() {
  const compacto = useCompacto();
  const { ler, filtrar } = useEstadoDeLista();

  /*
   * A aba na URL, como no Cadastro Assistido e nos Cadastros.
   *
   * **Sem isto, a aba não sobrevivia a nada**: fechar uma lacuna leva a pessoa à
   * Legislação para importar a fonte, e voltar ao Acervo devolvia "Jurisprudência
   * e doutrina" - a aba de onde ninguém tinha saído. O mesmo valia para recarregar
   * a página e para mandar o link a alguém: "abre o Acervo e clica na terceira
   * aba" é instrução que a barra de endereço deveria dar sozinha.
   *
   * A primeira aba é o padrão, e padrão não ocupa espaço na barra de endereço.
   */
  const aba = ler.texto('aba') ?? 'documentos';

  return (
    <Space orientation="vertical" size="large" style={{ width: '100%' }}>
      <div>
        <Typography.Title level={compacto ? 4 : 3} style={{ marginBottom: 4 }}>
          Acervo
        </Typography.Title>
        <Typography.Text type="secondary">
          O material que fundamenta e confere os comentários. Nada daqui é servido ao aluno.
        </Typography.Text>
      </div>

      <Tabs
        activeKey={aba}
        onChange={(chave) => filtrar({ aba: chave === 'documentos' ? undefined : chave })}
        items={[
          {
            key: 'documentos',
            label: 'Jurisprudência e doutrina',
            children: (
              <Space orientation="vertical" size="large" style={{ width: '100%' }}>
                <DocumentosDoAcervo />
              </Space>
            ),
          },
          {
            /*
             * Separado dos documentos porque **é outro tipo de acervo**, não outra
             * aba por comodidade: a lei entra por importação estruturada e é
             * alcançada por endereço exato, enquanto jurisprudência e doutrina
             * entram por upload e são alcançadas por busca (D-25). Misturá-las
             * numa lista só apagaria justamente a diferença que decide o que a
             * pipeline pode afirmar.
             */
            key: 'legislacao',
            label: 'Legislação',
            children: <LegislacaoDoAcervo />,
          },
          {
            /*
             * **A terceira aba é a fila de trabalho do acervo, e não mais um
             * relatório.** As duas primeiras mostram o que **entrou**; esta
             * mostra o que o Comentário Assistido procurou e **não achou** - e é
             * a única das três em que a ação seguinte é ingerir alguma coisa.
             * Fica aqui, junto do material, porque é aqui que se resolve.
             */
            key: 'lacunas',
            label: <BadgeDeLacunas />,
            children: <LacunasDoAcervo />,
          },
        ]}
      />
    </Space>
  );
}

/** Jurisprudência e doutrina: entram por upload e saem por busca (D-23). */
function DocumentosDoAcervo() {
  const compacto = useCompacto();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<{ title: string; source: string; series?: string; familia?: string }>();
  const [ultimoRelatorio, setUltimoRelatorio] = useState<CorpusIngestReport | null>(null);
  const [termo, setTermo] = useState('');
  const [buscado, setBuscado] = useState('');

  const documentos = useQuery({
    queryKey: ['corpus', 'documentos'],
    queryFn: () => api.listCorpusDocuments(),
  });

  const achados = useQuery({
    queryKey: ['corpus', 'busca', buscado],
    queryFn: () => api.searchCorpus({ termo: buscado }),
    enabled: buscado.length >= 2,
  });

  const invalidar = () => queryClient.invalidateQueries({ queryKey: ['corpus'] });

  const ingerir = useMutation({
    mutationFn: async (arquivo: File) => {
      const dados = await form.validateFields();
      return api.ingestCorpusDocument(arquivo, dados);
    },
    onSuccess: (relatorio) => {
      setUltimoRelatorio(relatorio);
      form.resetFields();
      void invalidar();
    },
    onError: (erro: Error) => message.error(erro.message),
  });

  const atualidade = useMutation({
    mutationFn: ({ id, desatualizado }: { id: string; desatualizado: boolean }) =>
      api.setCorpusDocumentCurrency(id, { desatualizado }),
    onSuccess: (_, { desatualizado }) => {
      message.success(
        desatualizado ? 'Documento fora da busca.' : 'Documento de volta à busca.',
      );
      void invalidar();
    },
    onError: (erro: Error) => message.error(erro.message),
  });

  const excluir = useMutation({
    mutationFn: (id: string) => api.deleteCorpusDocument(id),
    onSuccess: () => {
      message.success('Documento removido do acervo.');
      void invalidar();
    },
    onError: (erro: Error) => message.error(erro.message),
  });

  return (
    <Space orientation="vertical" size="large" style={{ width: '100%' }}>
      <Card title="Adicionar documento" size={compacto ? 'small' : undefined}>
        <Form form={form} layout="vertical" disabled={ingerir.isPending}>
          {/*
            As três larguras fixas somavam mais de 600px e vazavam do cartão no
            celular. Empilhados, cada campo ocupa a linha inteira - e o Título,
            que é o mais longo, deixa de ser o mais espremido.
          */}
          <Flex gap={16} wrap vertical={compacto}>
            <Form.Item
              name="title"
              label="Título"
              rules={[{ required: true, min: 3 }]}
              style={compacto ? { width: '100%', marginBottom: 0 } : { minWidth: 280, flex: 1 }}
            >
              <Input placeholder="Informativo de Jurisprudência n. 830" />
            </Form.Item>
            <Form.Item
              name="source"
              label="Origem"
              rules={[{ required: true, min: 2 }]}
              tooltip="Metade da identidade do registro: a mesma súmula do STF e do STJ são coisas diferentes."
              style={compacto ? { width: '100%', marginBottom: 0 } : { width: 140 }}
            >
              <Input placeholder="STJ" />
            </Form.Item>
            <Form.Item
              name="series"
              label="Série"
              tooltip="Quando o número sozinho não identifica. O Enunciado 1 existe em várias Jornadas."
              style={compacto ? { width: '100%', marginBottom: 0 } : { width: 220 }}
            >
              <Input placeholder="Notarial e Registral I" />
            </Form.Item>
            {/*
              Vazio é o normal: a assinatura decide, e ela acerta em todo o
              acervo de jurisprudência. Quem precisa disto é a prosa, que não tem
              assinatura - e o livro que **cita** enunciados, onde a detecção
              acerta a família errada e produz registro falso com cara de certo.
            */}
            <Form.Item
              name="familia"
              label="Família"
              tooltip="Deixe vazio para a assinatura decidir. Declare em livro de doutrina: prosa não tem assinatura, e um livro que cita enunciados entraria como se fosse a fonte deles."
              style={compacto ? { width: '100%', marginBottom: 0 } : { width: 180 }}
            >
              <Select
                allowClear
                placeholder="Pela assinatura"
                options={FAMILIAS_DECLARAVEIS.map((valor) => ({
                  value: valor,
                  label: FAMILIA[valor] ?? valor,
                }))}
              />
            </Form.Item>
          </Flex>
        </Form>

        <Upload.Dragger
          accept="application/pdf"
          maxCount={1}
          showUploadList={false}
          disabled={ingerir.isPending}
          beforeUpload={(arquivo) => {
            ingerir.mutate(arquivo as unknown as File);
            return false;
          }}
        >
          <p className="ant-upload-drag-icon">
            <UploadOutlined />
          </p>
          <p className="ant-upload-text">Arraste o PDF ou clique para escolher</p>
          <p className="ant-upload-hint">
            O acervo aceita qualquer PDF. O que o parser não reconhecer entra como texto, que
            continua consultável.
          </p>
        </Upload.Dragger>

        {ultimoRelatorio ? (
          <div style={{ marginTop: 16 }}>
            <RelatorioDaIngestao relatorio={ultimoRelatorio} />
          </div>
        ) : null}
      </Card>

      <Card
        title="Conferir no acervo"
        size={compacto ? 'small' : undefined}
        /*
          No celular a legenda desce para dentro do cartão: na linha do título,
          "É a mesma busca que a pipeline usa" ocupa duas linhas sozinha e fica
          maior que o nome do cartão que ela explica.
        */
        extra={
          compacto ? null : (
            <Typography.Text type="secondary">
              É a mesma busca que a pipeline usa.
            </Typography.Text>
          )
        }
      >
        <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
          {compacto ? (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              É a mesma busca que a pipeline usa.
            </Typography.Text>
          ) : null}
          <Input.Search
            placeholder="Termo a procurar"
            enterButton={<SearchOutlined />}
            value={termo}
            onChange={(evento) => setTermo(evento.target.value)}
            onSearch={setBuscado}
            allowClear
          />

          {buscado.length >= 2 && achados.data?.length === 0 ? (
            <Empty description="Nada encontrado. O acervo pode não ter esta fonte ainda." />
          ) : null}

          {achados.data?.map((achado: CorpusHit, indice: number) => (
            <Card key={`${achado.arquivo}-${indice}`} size="small">
              <Space orientation="vertical" size={4} style={{ width: '100%' }}>
                <Space size="small" wrap>
                  <Tooltip title={NIVEL[achado.tier]?.ajuda}>
                    <Tag color={NIVEL[achado.tier]?.cor}>{NIVEL[achado.tier]?.label}</Tag>
                  </Tooltip>
                  {achado.identity ? <Tag>{achado.identity}</Tag> : null}
                  <Typography.Text type="secondary">
                    {achado.fonte} · {achado.documento}
                    {achado.pagina ? ` · p. ${achado.pagina}` : ''}
                  </Typography.Text>
                </Space>
                <Typography.Paragraph
                  style={{ marginBottom: 0 }}
                  ellipsis={{ rows: 4, expandable: true, symbol: 'ver tudo' }}
                >
                  {achado.content}
                </Typography.Paragraph>
              </Space>
            </Card>
          ))}
        </Space>
      </Card>

      <Card title="Documentos" size={compacto ? 'small' : undefined}>
        {compacto ? (
          /*
            Oito colunas não encolhem em 390px, e a que sairia da vista numa
            rolagem horizontal é justamente a de ações - desatualizar e remover,
            que é o que traz alguém a esta lista. Empilhado, o documento fica no
            topo, as etiquetas de nível e situação logo abaixo, e as duas ações
            ficam sempre à mão.
          */
          <List<CorpusDocument>
            loading={documentos.isLoading}
            dataSource={documentos.data ?? []}
            pagination={paginacao(20)}
            locale={{ emptyText: <Empty description="Nenhum documento no acervo." /> }}
            renderItem={(linha) => (
              <List.Item style={{ paddingInline: 0, alignItems: 'flex-start' }}>
                <Flex vertical gap={8} style={{ width: '100%', minWidth: 0 }}>
                  <Flex vertical gap={0} style={{ minWidth: 0 }}>
                    <Typography.Text strong ellipsis>
                      {linha.title}
                    </Typography.Text>
                    <Typography.Text type="secondary" ellipsis style={{ fontSize: 12 }}>
                      {linha.fileName}
                    </Typography.Text>
                  </Flex>

                  <Space size={4} wrap>
                    <Tooltip title={NIVEL[linha.tier]?.ajuda}>
                      <Tag color={NIVEL[linha.tier]?.cor} style={{ marginInlineEnd: 0 }}>
                        {NIVEL[linha.tier]?.label ?? linha.tier}
                      </Tag>
                    </Tooltip>
                    {linha.outdatedAt ? (
                      <Tooltip title={linha.outdatedReason ?? 'Fora da busca'}>
                        <Tag color="orange" style={{ marginInlineEnd: 0 }}>
                          Desatualizado
                        </Tag>
                      </Tooltip>
                    ) : (
                      <Tag color="blue" style={{ marginInlineEnd: 0 }}>
                        Em uso
                      </Tag>
                    )}
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {linha.source} · {FAMILIA[linha.kind] ?? linha.kind} · {linha.recordCount}{' '}
                      registro(s)
                    </Typography.Text>
                  </Space>

                  {linha.importedAt ? (
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      Adicionado em {formatarData(linha.importedAt)}
                    </Typography.Text>
                  ) : null}

                  <Space size="small">
                    <Button
                      size="small"
                      aria-label={
                        linha.outdatedAt
                          ? 'Devolver documento à busca'
                          : 'Marcar documento como desatualizado'
                      }
                      loading={atualidade.isPending}
                      onClick={() =>
                        atualidade.mutate({ id: linha.id, desatualizado: !linha.outdatedAt })
                      }
                    >
                      {linha.outdatedAt ? 'Devolver à busca' : 'Desatualizar'}
                    </Button>
                    <Popconfirm
                      title="Remover do acervo?"
                      description="Os registros e trechos deste documento saem junto."
                      okText="Remover"
                      cancelText="Cancelar"
                      onConfirm={() => excluir.mutate(linha.id)}
                    >
                      <Button
                        danger
                        size="small"
                        icon={<DeleteOutlined />}
                        aria-label="Remover documento do acervo"
                      >
                        Remover
                      </Button>
                    </Popconfirm>
                  </Space>

                  {/*
                    O relatório fica aberto no celular. A linha expansível da
                    tabela não sobrevive à lista, e esconder atrás de um toque o
                    diagnóstico que diz se o parser acertou seria esconder a
                    única coisa que a tela sabe e a contagem não conta.
                  */}
                  {linha.report ? <RelatorioDaIngestao relatorio={linha.report} /> : null}
                </Flex>
              </List.Item>
            )}
          />
        ) : (
        <Table<CorpusDocument>
          rowKey="id"
          loading={documentos.isLoading}
          dataSource={documentos.data ?? []}
          pagination={paginacao(20)}
          columns={[
            {
              title: 'Documento',
              dataIndex: 'title',
              render: (titulo: string, linha) => (
                <Space orientation="vertical" size={0}>
                  <Typography.Text strong>{titulo}</Typography.Text>
                  <Typography.Text type="secondary">{linha.fileName}</Typography.Text>
                </Space>
              ),
            },
            { title: 'Origem', dataIndex: 'source', width: 110 },
            {
              title: 'Família',
              dataIndex: 'kind',
              width: 140,
              render: (kind: string) => FAMILIA[kind] ?? kind,
            },
            {
              title: 'Nível',
              dataIndex: 'tier',
              width: 120,
              render: (tier: string) => (
                <Tooltip title={NIVEL[tier]?.ajuda}>
                  <Tag color={NIVEL[tier]?.cor}>{NIVEL[tier]?.label}</Tag>
                </Tooltip>
              ),
            },
            { title: 'Registros', dataIndex: 'recordCount', width: 100 },
            {
              title: 'Situação',
              width: 160,
              render: (_, linha) =>
                linha.outdatedAt ? (
                  <Tooltip title={linha.outdatedReason ?? 'Fora da busca'}>
                    <Tag color="orange">Desatualizado</Tag>
                  </Tooltip>
                ) : (
                  <Tag color="blue">Em uso</Tag>
                ),
            },
            {
              title: 'Adicionado',
              dataIndex: 'importedAt',
              width: 150,
              render: (data: string | null) => (data ? formatarData(data) : '-'),
            },
            {
              title: 'Ações',
              width: 220,
              render: (_, linha) => (
                <Space size="small">
                  <Button
                    size="small"
                    aria-label={
                      linha.outdatedAt
                        ? 'Devolver documento à busca'
                        : 'Marcar documento como desatualizado'
                    }
                    loading={atualidade.isPending}
                    onClick={() =>
                      atualidade.mutate({ id: linha.id, desatualizado: !linha.outdatedAt })
                    }
                  >
                    {linha.outdatedAt ? 'Devolver à busca' : 'Desatualizar'}
                  </Button>
                  <Popconfirm
                    title="Remover do acervo?"
                    description="Os registros e trechos deste documento saem junto."
                    okText="Remover"
                    cancelText="Cancelar"
                    onConfirm={() => excluir.mutate(linha.id)}
                  >
                    <Button
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                      aria-label="Remover documento do acervo"
                    />
                  </Popconfirm>
                </Space>
              ),
            },
          ]}
          expandable={{
            rowExpandable: (linha) => Boolean(linha.report),
            expandedRowRender: (linha) =>
              linha.report ? <RelatorioDaIngestao relatorio={linha.report} /> : null,
          }}
        />
        )}
      </Card>
    </Space>
  );
}
